import React, { useEffect, useState, useCallback } from "react";
import Cookies from "js-cookie";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import Loader from "../components/Loader";
import { crmHeaders } from "../utils/crmApi";
import { useEmailNotifications } from "../context/EmailNotificationContext";
import { useEmailHealth } from "../context/EmailHealthContext";
import { hasFullAccess, hasAnyEmailAccess } from "../utils/userAccess";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const ALERT_META = {
    holiday: { label: "Public Holiday", icon: "bi-calendar-event", className: "crm-country-alert--holiday" },
    strike: { label: "Strike", icon: "bi-exclamation-octagon", className: "crm-country-alert--strike" },
    closure: { label: "Office Closed", icon: "bi-building-x", className: "crm-country-alert--closure" },
    news: { label: "News", icon: "bi-newspaper", className: "crm-country-alert--news" },
    other: { label: "Notice", icon: "bi-info-circle", className: "crm-country-alert--other" },
};

export default function AdminDashboard() {
    const getUsername = Cookies.get("username");
    const isAdmin = hasFullAccess();
    const canSeeEmailHealth = isAdmin || hasAnyEmailAccess();
    const { totalUnread } = useEmailNotifications();
    const { issueCount, blockedCount, healthByEmail } = useEmailHealth();

    const [stats, setStats] = useState(null);
    const [leadsCount, setLeadsCount] = useState(0);
    const [activeUsers, setActiveUsers] = useState(0);
    const [loginLogs, setLoginLogs] = useState([]);
    const [countryStatus, setCountryStatus] = useState(null);
    const [showAlertModal, setShowAlertModal] = useState(false);
    const [alertForm, setAlertForm] = useState({
        country: "",
        alertType: "strike",
        title: "",
        description: "",
        eventDate: "",
        endDate: "",
    });
    const [savingAlert, setSavingAlert] = useState(false);
    const [loading, setLoading] = useState(true);

    const loadCountryStatus = useCallback(() => {
        return axios
            .get(`${BASE_URL}/getcountrystatus`, { headers: crmHeaders() })
            .then((res) => setCountryStatus(res.data))
            .catch((error) => console.error("Country status load error:", error));
    }, []);

    useEffect(() => {
        const requests = [
            axios.get(`${BASE_URL}/getdashboardstats`, { headers: crmHeaders() }),
            axios.get(`${BASE_URL}/getleads`, { headers: crmHeaders() }),
            axios.get(`${BASE_URL}/getcountrystatus`, { headers: crmHeaders() }),
        ];

        if (isAdmin) {
            requests.push(axios.get(`${BASE_URL}/getactiveusers`, { headers: crmHeaders() }));
            requests.push(axios.get(`${BASE_URL}/getloginhistory?limit=50`, { headers: crmHeaders() }));
        }

        Promise.all(requests)
            .then((results) => {
                const statsRes = results[0];
                const leadsRes = results[1];
                const countryRes = results[2];
                const activeRes = isAdmin ? results[3] : null;
                const loginRes = isAdmin ? results[4] : null;

                setStats(statsRes.data);
                setLeadsCount(Array.isArray(leadsRes.data) ? leadsRes.data.length : 0);
                setCountryStatus(countryRes.data);
                if (activeRes) {
                    setActiveUsers(activeRes.data?.number || 0);
                }
                if (loginRes && Array.isArray(loginRes.data)) {
                    setLoginLogs(loginRes.data);
                }
            })
            .catch((error) => console.error("Dashboard load error:", error))
            .finally(() => setLoading(false));
    }, [isAdmin]);

    const formatEventDate = (start, end) => {
        if (!start) return "—";
        const opts = { day: "numeric", month: "short", year: "numeric" };
        const s = new Date(`${start}T12:00:00`).toLocaleDateString(undefined, opts);
        if (end && end !== start) {
            const e = new Date(`${end}T12:00:00`).toLocaleDateString(undefined, opts);
            return `${s} – ${e}`;
        }
        return s;
    };

    const openAddAlert = (countryName = "") => {
        setAlertForm({
            country: countryName || countryStatus?.countries?.[0]?.country || "",
            alertType: "strike",
            title: "",
            description: "",
            eventDate: new Date().toISOString().slice(0, 10),
            endDate: "",
        });
        setShowAlertModal(true);
    };

    const submitAlert = async (e) => {
        e.preventDefault();
        if (!alertForm.country || !alertForm.title || !alertForm.eventDate) {
            toast.error("Country, title, and date are required");
            return;
        }
        setSavingAlert(true);
        try {
            await axios.post(`${BASE_URL}/addcountryalert`, alertForm, { headers: crmHeaders() });
            toast.success("Country alert added");
            setShowAlertModal(false);
            await loadCountryStatus();
        } catch (error) {
            toast.error(error.response?.data?.message || "Could not save alert");
        } finally {
            setSavingAlert(false);
        }
    };

    const removeAlert = async (alert) => {
        if (alert.source !== "manual" || typeof alert.id !== "number") return;
        if (!window.confirm(`Remove "${alert.title}"?`)) return;
        try {
            await axios.delete(`${BASE_URL}/deletecountryalert/${alert.id}`, { headers: crmHeaders() });
            toast.success("Alert removed");
            await loadCountryStatus();
        } catch (error) {
            toast.error(error.response?.data?.message || "Could not remove alert");
        }
    };

    if (loading) return <Loader />;

    const totalUserFollowUps = stats?.totalUserFollowUps ?? 0;

    const formatLoginDate = (value) => {
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return { day: "—", time: "—" };
        return {
            day: d.toLocaleDateString(undefined, {
                weekday: "short",
                day: "numeric",
                month: "short",
                year: "numeric",
            }),
            time: d.toLocaleTimeString(undefined, {
                hour: "2-digit",
                minute: "2-digit",
            }),
        };
    };

    const deviceIcon = (type) => {
        if (type === "Mobile") return "bi-phone";
        if (type === "Tablet") return "bi-tablet";
        return "bi-laptop";
    };

    const deviceBadgeClass = (type) => {
        if (type === "Mobile") return "crm-login-device--mobile";
        if (type === "Tablet") return "crm-login-device--tablet";
        return "crm-login-device--desktop";
    };

    return (
        <div className="container">
            <div className="crm-page-header">
                <h1 className="crm-page-title">Welcome back, {getUsername}</h1>
                <p className="crm-page-subtitle">Here&apos;s an overview of your CRM portal</p>
            </div>

            {canSeeEmailHealth && issueCount > 0 && (
                <div
                    className={`alert ${blockedCount > 0 ? "alert-danger" : "alert-warning"} mb-4`}
                    role="alert"
                >
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    <strong>
                        {blockedCount > 0 ? blockedCount : issueCount} email account(s)
                    </strong>{" "}
                    par mail / domain issue hai.
                    <ul className="mb-0 mt-2 small">
                        {Object.values(healthByEmail)
                            .filter((h) => h.status !== "ok")
                            .slice(0, 5)
                            .map((h) => (
                                <li key={h.email}>
                                    <strong>{h.email}</strong>
                                    {" — "}
                                    {h.alerts?.find((a) => a.level === "error")?.message ||
                                        h.alerts?.find((a) => a.level === "warning")?.message ||
                                        "Issue detected"}
                                </li>
                            ))}
                    </ul>
                    <Link to="/incoming-emails" className="alert-link d-inline-block mt-2">
                        Inbox kholo →
                    </Link>
                </div>
            )}

            <div className="crm-stat-grid">
                {isAdmin && (
                    <Link to="/users" className="crm-stat-card">
                        <div className="crm-stat-card-icon crm-stat-card-icon--blue">
                            <i className="bi bi-people-fill"></i>
                        </div>
                        <div className="crm-stat-card-label">Total Users</div>
                        <div className="crm-stat-card-value">{stats?.totalUsers ?? 0}</div>
                        <span className="crm-stat-card-link">
                            Manage Users <i className="bi bi-arrow-right"></i>
                        </span>
                    </Link>
                )}

                {isAdmin && (
                    <Link to="/countries" className="crm-stat-card">
                        <div className="crm-stat-card-icon crm-stat-card-icon--green">
                            <i className="bi bi-globe-americas"></i>
                        </div>
                        <div className="crm-stat-card-label">Total Countries</div>
                        <div className="crm-stat-card-value">{stats?.totalCountries ?? 0}</div>
                        <span className="crm-stat-card-link">
                            Manage Countries <i className="bi bi-arrow-right"></i>
                        </span>
                    </Link>
                )}

                <Link to="/emails" className="crm-stat-card">
                    <div className="crm-stat-card-icon crm-stat-card-icon--teal">
                        <i className="bi bi-envelope-at-fill"></i>
                    </div>
                    <div className="crm-stat-card-label">Email Accounts</div>
                    <div className="crm-stat-card-value">{stats?.totalEmailAccounts ?? 0}</div>
                    <span className="crm-stat-card-link">
                        Manage Emails <i className="bi bi-arrow-right"></i>
                    </span>
                </Link>

                <Link to="/incoming-emails" className="crm-stat-card">
                    <div className="crm-stat-card-icon crm-stat-card-icon--red">
                        <i className="bi bi-envelope-exclamation-fill"></i>
                    </div>
                    <div className="crm-stat-card-label">Unread Emails</div>
                    <div className="crm-stat-card-value">{totalUnread}</div>
                    <span className="crm-stat-card-link">
                        Open Inbox <i className="bi bi-arrow-right"></i>
                    </span>
                </Link>

                <Link to="/follow-ups" className="crm-stat-card">
                    <div className="crm-stat-card-icon crm-stat-card-icon--orange">
                        <i className="bi bi-journal-check"></i>
                    </div>
                    <div className="crm-stat-card-label">Team Follow-Ups</div>
                    <div className="crm-stat-card-value">{totalUserFollowUps}</div>
                    <span className="crm-stat-card-link">
                        View all follow-ups <i className="bi bi-arrow-right"></i>
                    </span>
                </Link>

                <div className="crm-stat-card">
                    <div className="crm-stat-card-icon crm-stat-card-icon--purple">
                        <i className="bi bi-person-lines-fill"></i>
                    </div>
                    <div className="crm-stat-card-label">Total Leads</div>
                    <div className="crm-stat-card-value">{leadsCount}</div>
                    {isAdmin && (
                        <Link to="/allleads" className="crm-stat-card-link">
                            Manage Leads <i className="bi bi-arrow-right"></i>
                        </Link>
                    )}
                </div>

                {isAdmin && (
                    <div className="crm-stat-card">
                        <div className="crm-stat-card-icon crm-stat-card-icon--pink">
                            <i className="bi bi-activity"></i>
                        </div>
                        <div className="crm-stat-card-label">Active Users Now</div>
                        <div className="crm-stat-card-value">{activeUsers}</div>
                        <span className="crm-stat-card-link text-muted">Live session count</span>
                    </div>
                )}

                <div className="crm-stat-card">
                    <div className="crm-stat-card-icon crm-stat-card-icon--amber">
                        <i className="bi bi-calendar2-week"></i>
                    </div>
                    <div className="crm-stat-card-label">Upcoming Off Days</div>
                    <div className="crm-stat-card-value">{countryStatus?.totalUpcoming ?? 0}</div>
                    <span className="crm-stat-card-link text-muted">
                        Next {countryStatus?.lookaheadDays ?? 60} days · all countries
                    </span>
                </div>
            </div>

            <div className="crm-dashboard-panel crm-country-status-panel">
                <div className="crm-dashboard-panel-header">
                    <h5>
                        <i className="bi bi-globe2 me-2"></i>
                        Country Holidays &amp; Alerts
                    </h5>
                    <div className="crm-country-status-header-actions">
                        <span className="crm-login-activity-count">
                            {countryStatus?.countries?.length ?? 0} active countries
                        </span>
                        {isAdmin && (
                            <button type="button" className="crm-country-add-alert-btn" onClick={() => openAddAlert()}>
                                <i className="bi bi-plus-lg"></i> Add strike / news
                            </button>
                        )}
                    </div>
                </div>
                <div className="crm-dashboard-panel-body crm-country-status-body">
                    {!countryStatus?.countries?.length ? (
                        <div className="crm-login-activity-empty">
                            <i className="bi bi-globe"></i>
                            <p>No active countries found. Add countries from the Countries menu.</p>
                        </div>
                    ) : (
                        <div className="crm-country-status-grid">
                            {countryStatus.countries.map((item) => (
                                <div key={item.country} className="crm-country-status-card">
                                    <div className="crm-country-status-card-head">
                                        <span className="crm-country-status-flag">
                                            <i className="bi bi-flag-fill"></i>
                                        </span>
                                        <div>
                                            <h6>{item.country}</h6>
                                            <span className="crm-country-status-sub">
                                                {item.upcomingCount
                                                    ? `${item.upcomingCount} upcoming`
                                                    : "No upcoming off days"}
                                            </span>
                                        </div>
                                        {isAdmin && (
                                            <button
                                                type="button"
                                                className="crm-country-status-add-mini"
                                                title="Add alert for this country"
                                                onClick={() => openAddAlert(item.country)}
                                            >
                                                <i className="bi bi-plus"></i>
                                            </button>
                                        )}
                                    </div>
                                    {item.alerts.length === 0 ? (
                                        <p className="crm-country-status-clear">
                                            <i className="bi bi-check-circle me-1"></i>
                                            All clear for next {countryStatus.lookaheadDays} days
                                        </p>
                                    ) : (
                                        <ul className="crm-country-alert-list">
                                            {item.alerts.map((alert) => {
                                                const meta = ALERT_META[alert.alertType] || ALERT_META.other;
                                                return (
                                                    <li key={String(alert.id)} className="crm-country-alert-item">
                                                        <span className={`crm-country-alert-badge ${meta.className}`}>
                                                            <i className={`bi ${meta.icon}`}></i>
                                                            {meta.label}
                                                        </span>
                                                        <div className="crm-country-alert-main">
                                                            <strong>{alert.title}</strong>
                                                            {alert.description && (
                                                                <span className="crm-country-alert-desc">{alert.description}</span>
                                                            )}
                                                            <span className="crm-country-alert-date">
                                                                <i className="bi bi-calendar3 me-1"></i>
                                                                {formatEventDate(alert.eventDate, alert.endDate)}
                                                                {alert.source === "auto" && (
                                                                    <span className="crm-country-alert-auto"> · auto</span>
                                                                )}
                                                            </span>
                                                        </div>
                                                        {isAdmin && alert.source === "manual" && typeof alert.id === "number" && (
                                                            <button
                                                                type="button"
                                                                className="crm-country-alert-remove"
                                                                onClick={() => removeAlert(alert)}
                                                                title="Remove"
                                                            >
                                                                <i className="bi bi-x-lg"></i>
                                                            </button>
                                                        )}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {isAdmin && showAlertModal && (
                <div className="crm-modal-overlay" onClick={() => !savingAlert && setShowAlertModal(false)}>
                    <div className="crm-modal crm-country-alert-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="crm-modal-header">
                            <h5>
                                <i className="bi bi-megaphone me-2"></i>
                                Add Country Alert
                            </h5>
                            <button type="button" className="crm-modal-close" onClick={() => setShowAlertModal(false)}>
                                <i className="bi bi-x-lg"></i>
                            </button>
                        </div>
                        <form onSubmit={submitAlert} className="crm-modal-body">
                            <div className="mb-3">
                                <label className="form-label">Country</label>
                                <select
                                    className="form-select"
                                    value={alertForm.country}
                                    onChange={(e) => setAlertForm({ ...alertForm, country: e.target.value })}
                                    required
                                >
                                    <option value="">Select country</option>
                                    {countryStatus?.countries?.map((c) => (
                                        <option key={c.country} value={c.country}>
                                            {c.country}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Type</label>
                                <select
                                    className="form-select"
                                    value={alertForm.alertType}
                                    onChange={(e) => setAlertForm({ ...alertForm, alertType: e.target.value })}
                                >
                                    <option value="strike">Strike</option>
                                    <option value="closure">Office / Court Closed</option>
                                    <option value="holiday">Public Holiday (manual)</option>
                                    <option value="news">News / Announcement</option>
                                    <option value="other">Other notice</option>
                                </select>
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Title</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="e.g. National strike — offices closed"
                                    value={alertForm.title}
                                    onChange={(e) => setAlertForm({ ...alertForm, title: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Details (optional)</label>
                                <textarea
                                    className="form-control"
                                    rows={3}
                                    placeholder="Extra info for your team..."
                                    value={alertForm.description}
                                    onChange={(e) => setAlertForm({ ...alertForm, description: e.target.value })}
                                />
                            </div>
                            <div className="row g-2 mb-3">
                                <div className="col-6">
                                    <label className="form-label">Start date</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={alertForm.eventDate}
                                        onChange={(e) => setAlertForm({ ...alertForm, eventDate: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="col-6">
                                    <label className="form-label">End date (optional)</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={alertForm.endDate}
                                        onChange={(e) => setAlertForm({ ...alertForm, endDate: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="crm-modal-footer">
                                <button type="button" className="btn btn-light" onClick={() => setShowAlertModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary" disabled={savingAlert}>
                                    {savingAlert ? "Saving..." : "Save alert"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {isAdmin && (
                <div className="crm-dashboard-panel crm-login-activity-panel">
                    <div className="crm-dashboard-panel-header">
                        <h5>
                            <i className="bi bi-shield-lock me-2"></i>
                            Recent Login Activity
                        </h5>
                        <span className="crm-login-activity-count">
                            {loginLogs.length} recent session{loginLogs.length !== 1 ? "s" : ""}
                        </span>
                    </div>
                    <div className="crm-dashboard-panel-body crm-login-activity-body">
                        {loginLogs.length === 0 ? (
                            <div className="crm-login-activity-empty">
                                <i className="bi bi-door-open"></i>
                                <p>No login records yet. Activity will appear after users sign in.</p>
                            </div>
                        ) : (
                            <table className="table crm-login-activity-table mb-0">
                                <thead>
                                    <tr>
                                        <th>User</th>
                                        <th>Date &amp; Time</th>
                                        <th>Location</th>
                                        <th>Device</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loginLogs.map((row) => {
                                        const when = formatLoginDate(row.Login_At);
                                        const initial = (row.Username || "?").charAt(0).toUpperCase();
                                        return (
                                            <tr key={row.Id}>
                                                <td>
                                                    <div className="crm-login-user">
                                                        <span className="crm-login-avatar">{initial}</span>
                                                        <div>
                                                            <div className="crm-login-username">{row.Username}</div>
                                                            <span className="crm-login-role">{row.Role || "User"}</span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="crm-login-when">
                                                        <span className="crm-login-day">{when.day}</span>
                                                        <span className="crm-login-time">
                                                            <i className="bi bi-clock me-1"></i>
                                                            {when.time}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="crm-login-location">
                                                        <i className="bi bi-geo-alt-fill me-1"></i>
                                                        {row.Location_Label || row.Ip_Address || "—"}
                                                    </div>
                                                </td>
                                                <td>
                                                    <span
                                                        className={`crm-login-device ${deviceBadgeClass(row.Device_Type)}`}
                                                    >
                                                        <i className={`bi ${deviceIcon(row.Device_Type)} me-1`}></i>
                                                        {row.Device_Type || "Desktop"}
                                                    </span>
                                                    <div className="crm-login-device-detail">
                                                        {row.Browser} · {row.Os_Name}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}

        </div>
    );
}
